import { DOCUMENT } from '@angular/common'
import {
  AfterViewInit,
  Component,
  ElementRef,
  Inject,
  inject,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core'
import { ResizeObserverService } from '@vg-constellation/angular-16/core'
import { TooltipComponent } from '@vg-constellation/angular-16/tooltip'
import { TableSortEvent } from '@vg-constellation/patterns-angular/data-table'
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs'
import { adobeLaunch } from 'vg-iig-adobe-launch'

import {
  ADJEXPRTPC,
  ADJWTDEXPC,
  CMLT,
  COLLECTIVE_INVESTMENT_TRUST,
  DATA_UNAVAILABLE,
  EM_DASH,
  FUND_NAME,
  FUND_OF_FUND,
  MARKET,
  MONTH_END_RETURN,
  MONTH_END_RETURN_MARKET_ETF,
  MONTH_END_RETURN_NAV,
  NOT_AVAILABLE,
  OPEN_FACT_SHEET,
  PERCENT_SIGN,
  REAL_ESTATE,
  RFEE,
  RMKT,
  RTOT,
  SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
  TARGET,
  TRUST,
  UNKNOWN,
  YTD_RETURN,
  YTD_RETURN_VAL,
} from '../../../constants/constant.content'
import {
  NUMBER_HUNDRED,
  NUMBER_MINUS_ONE,
} from '../../../constants/constant.numbers'
import { PRODUCT_DETAILS_URL } from '../../../constants/constant.urls'
import { IDisplayTableModel } from '../../models/display-table-model'
import { IPerformanceAlphabeticalModel } from '../../models/performance-alphabetical-model'
import { IProduct } from '../../models/product'
import { ClassificationRulesService } from '../../services/classification-rules/classification-rules.service'
import { AbstractFilterProcess } from '../../services/data-filters/abstract-filter-process'
import { FundClassificationProcess } from '../../services/data-filters/fund-classification-process'
import { IFilterState } from '../../services/data-filters/models/ifilter-state'
import { FundRuleName } from '../../services/data-filters/rules/fund-rule-name'
import { FundLiteratureContentService } from '../../services/fund-literature/fund-literature.service'
import { ObservableService } from '../../services/observable-service/observable.service'
import { PolarisDataService } from '../../services/polaris-service/polaris-data-service'
import { SecYieldService } from '../../services/sec-yield/sec-yield.service'
import { SharedClassService } from '../../services/shared-class/shared-class.service'
import { UtilityService } from '../../services/utility-service/utility-service.service'
import { FilterModel } from '../../shared/filter-model'
import { SortPipe } from '../../shared/pipe/sort.pipe'

@Component({
  selector: 'performance-table',
  templateUrl: './performance-table.component.html',
  styleUrls: ['./performance-table.component.scss'],
})
export class PerformanceTableComponent implements OnInit, AfterViewInit {
  @ViewChild('tooltipRef') tooltipRef: TooltipComponent
  @ViewChild('secYieldTooltipRef') secYieldTooltipRef: TooltipComponent
  @Input() performanceCaas: any
  static multiAssetTargetDateSet: Set<string> = new Set<string>()
  hoveredRowIndex: number | null = null
  isShowTable: boolean
  isShowLoader: boolean
  isDataAvailable: boolean
  annualAsOfDt: undefined
  ytdAsOf: undefined
  performancePolarisContent: IProduct[]
  performanceData: IPerformanceAlphabeticalModel[] = []
  performanceView: IDisplayTableModel[] = []
  isSharedClass = false
  returnType = 'monthEndReturn'
  displayDataType: string
  updateMessage: string
  footNoteCodesMap: Map<string, string> = new Map<string, string>()
  assetViewList = []
  retirementSavingsTrustShareClassesList = []
  fundClassificationMap: any

  fundClassificationProcess: FundClassificationProcess
  secYield: SecYieldService
  sharedClass: SharedClassService
  isBenchmark = false
  aemCassDataFromSession: string
  fundNameData = ''
  fundCaasData = ''
  fundPolarisData = ''
  notePopupPara: ''
  productDetails = PRODUCT_DETAILS_URL
  sortBy = FUND_NAME
  isREIT = false
  useContentWidth = false

  filterModel: FilterModel
  sortByPipe: SortPipe
  fd3FundLiteratureContentService: FundLiteratureContentService
  factSheetMap: Map<string, string> = new Map<string, string>()
  isFundLiteratureDataAvailable = true
  factSheetTooltip = OPEN_FACT_SHEET
  emDash = EM_DASH
  dataUnavailable = DATA_UNAVAILABLE
  hovered = false
  viewType: string
  ros: ResizeObserverService
  utilityService: UtilityService
  NOT_AVAILABLE = NOT_AVAILABLE

  @ViewChild('groupHeaderRef', { read: ElementRef }) groupHeaderRef!: ElementRef
  secondHeaderRowStickyTop!: string
  private _destory = new Subject<void>()
  constructor(
    private readonly polarisDataService: PolarisDataService,
    private readonly filterProcess: AbstractFilterProcess,
    private readonly observableService: ObservableService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.filterModel = inject(FilterModel)
    this.sortByPipe = inject(SortPipe)
    this.utilityService = inject(UtilityService)
    this.ros = inject(ResizeObserverService)
    this.assetViewList = []
    this.fundClassificationProcess = inject(FundClassificationProcess)
    this.fd3FundLiteratureContentService = inject(FundLiteratureContentService)
    this.secYield = inject(SecYieldService)
    this.sharedClass = inject(SharedClassService)
  }

  ngOnInit() {
    this.initializeLoader()
    this.loadSessionData()
    this.loadFactSheetData()
    this.setupSearchEvent()
    this.setupFilterSubscription()
    this.setupViewTypeSubscription()
  }

  private initializeLoader() {
    this.isShowLoader = true
  }

  private loadSessionData() {
    this.aemCassDataFromSession = sessionStorage.getItem(
      SESSION_STORAGE_CONSTANTS_CAAS_CONTENT,
    )
    this.performanceCaas = JSON.parse(
      this.aemCassDataFromSession,
    ).data.fd3tablecontentList.items[0]
  }

  private loadFactSheetData() {
    this.factSheetMap = this.fd3FundLiteratureContentService.getFactSheetData()
    this.isFundLiteratureDataAvailable = this.factSheetMap.size > 0
  }

  private setupSearchEvent() {
    this.observableService.searchSelectedValue
      .pipe(debounceTime(NUMBER_HUNDRED), distinctUntilChanged())
      .subscribe((searchText: string) => {
        this.filterProcess.setSearchText(searchText)
        this.applyFilter()
      })
  }

  private setupFilterSubscription() {
    this.observableService.consolidateFiltersObservable.subscribe(
      (filterState: IFilterState) => {
        this.isSharedClass = filterState.isShareClassSelected()
        this.isBenchmark = filterState.isBenchMarkSelected()
        this.fundClassificationMap =
          this.fundClassificationProcess.getFundClassificationMap()

        if (this.isMultiAssetTargetDateAvailable()) {
          this.populateMultiAssetTargetDateSet()
        }
        this.fetchPerformanceData(filterState.getDisplaySelection() || '')
      },
    )
  }

  private isMultiAssetTargetDateAvailable(): boolean {
    return (
      this.fundClassificationMap.size > 0 &&
      this.fundClassificationMap.get(FundRuleName.MULTI_ASSET_TARGET_DATE) &&
      this.fundClassificationMap.get(FundRuleName.MULTI_ASSET_TARGET_DATE)
        .fundDtlAry
    )
  }

  private populateMultiAssetTargetDateSet() {
    for (const object of this.fundClassificationMap.get(
      FundRuleName.MULTI_ASSET_TARGET_DATE,
    ).fundDtlAry) {
      PerformanceTableComponent.multiAssetTargetDateSet.add(object.portId)
    }
  }

  private setupViewTypeSubscription() {
    this.observableService.viewTypeValue.subscribe((value) => {
      this.sortBy = FUND_NAME
      this.viewType = value
    })
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.handleResize()
    })
  }

  handleResize(): void {
    this.ros
      .observe(this.document.body)
      .pipe(takeUntil(this._destory))
      .subscribe(() => {
        if (this.groupHeaderRef) {
          const { offsetHeight } = this.groupHeaderRef.nativeElement
          this.secondHeaderRowStickyTop = `${offsetHeight}px`
        }
      })
  }

  /**
   *     fetchPerformanceData
   *     Fetch Performance Data from AppComponent static variable
   * */
  // eslint-disable-next-line max-statements
  fetchPerformanceData(type: string) {
    this.isShowTable = false
    this.isShowLoader = false
    if (type.indexOf(MONTH_END_RETURN_NAV) > NUMBER_MINUS_ONE) {
      this.displayDataType = 'NAV'
    } else if (type.indexOf(MONTH_END_RETURN_MARKET_ETF) > NUMBER_MINUS_ONE) {
      this.displayDataType = MARKET
    } else {
      return
    }
    const productList = this.polarisDataService.getProductList()
    if (productList !== undefined) {
      this.isShowLoader = true
      this.isDataAvailable = true
      this.annualAsOfDt = undefined
      this.ytdAsOf = undefined
      this.performancePolarisContent = productList
      this.readPerformanceData(this.performancePolarisContent, type)
    } else {
      this.isShowTable = true
      this.isShowLoader = false
      this.isDataAvailable = false
    }
  }

  /**
   *     readPerformanceData
   *     Read Performance Data from AppComponent static variable
   *     Apply Filter and create view
   * */
  readPerformanceData(performanceData: IProduct[], type: string) {
    this.getPerformanceData(
      performanceData,
      type,
      (quarterData: IPerformanceAlphabeticalModel[]) => {
        this.performanceData = quarterData
        this.isShowTable = true
        this.isShowLoader = false
        this.applyFilter()
        this.updateAnnouncement()
      },
    )
  }

  applyFilter() {
    const view = this.filterProcess.applyFilter(this.performanceData)
    this.performanceView = view.getDispalyTableModelAry()
    this.performanceView = this.sortByPipe.transform(
      this.performanceView,
      FUND_NAME,
    )
    this.observableService.changeRowRecordValue(this.performanceView.length)
    this.updateAnnouncement()
  }

  /**
   *     getPerformanceData
   *     Get Performance Data and iterate
   *     to set values for each
   * */
  getPerformanceData(
    performanceQuarterData: any,
    type: string,
    callback: { (quarterData: any): void; (arg0: any[]): void },
  ) {
    let performanceData = [],
      result: any
    if (performanceQuarterData) {
      for (const productMap of performanceQuarterData) {
        result = this.setPolarisPerformanceData(productMap, type)
        if (result) {
          performanceData = [...performanceData, ...result]
        }
      }
    }
    callback(performanceData)
  }

  /**
   *     setPolarisPerformanceData
   *     Set Data for each fund(Benchmark and  Performance)
   *     to set values for each
   * */
  setPolarisPerformanceData(performanceDetailsMap: any, type: string): any {
    let performanceData = []
    performanceData.pop()
    if (performanceDetailsMap !== undefined) {
      performanceDetailsMap = this.getFundBenchmarkShareClassData(
        performanceDetailsMap,
      )
      performanceData = this.setPolarisFundReturnsData(
        performanceDetailsMap,
        performanceData,
        type,
      )
    }
    this.isShowLoader = false
    return performanceData
  }

  /**
   *     getFundBenchmarkShareClassData
   *     Filtering of Data
   * */
  getFundBenchmarkShareClassData(fund: any) {
    if (!this.isSharedClass) {
      this.retirementSavingsTrustShareClassesList =
        this.sharedClass.retirementSavingsTrustShareClasses(fund)
    }
    return fund
  }

  /**
   *     setPolarisFundReturnsData
   *     Set Data for each fund(Performance) after filtering sharedClass
   *     to set values for each
   * */
  // eslint-disable-next-line complexity,max-statements
  setPolarisFundReturnsData(
    performanceDetailsMap: any,
    performanceData: any[],
    type: string,
  ) {
    const fund = performanceDetailsMap
    if (
      !this.isSharedClass ? this.sharedClass.checkForShareClasses(fund) : true
    ) {
      if (fund.managementStrategy !== UNKNOWN) {
        let performanceReturns: any = {}
        performanceReturns.secYield = {}
        performanceReturns.benchmark = {}
        const trimFund = this.utilityService.trimPolarisFundName(fund)
        performanceReturns.fundName = trimFund.fundName
        performanceReturns.closedIndicator =
          this.utilityService.getClosedIndicatorForFund(fund)
        performanceReturns.symbol = trimFund.fundSymbol
        performanceReturns.fundID = fund.portId
        performanceReturns.getFundId = function () {
          return fund.portId
        }
        performanceReturns.sinceInceptionRtnDate = fund.fundInceptionDate
        performanceReturns.expenseRatio = this.setPolarisExpenseRatio(fund)
        performanceReturns.factSheetUrl = this.setFactSheetData(fund)
        performanceReturns = this.utilityService.setPolarisFees(
          fund,
          performanceReturns,
        )
        this.setPolarisPerformanceReturns(fund, performanceReturns, type)
        this.setPolarisYTDReturnsData(fund, performanceReturns)
        this.setPolarisYieldsData(fund, performanceReturns)
        if (fund.benchmark) {
          this.setPolarisBenchmarkReturns(fund, performanceReturns, type)
        }
        performanceData.push(performanceReturns)
      }
    }
    return performanceData
  }

  /**
   *     setPolarisExpenseRatio
   *     Set expenseRatio data for each fund
   * */
  // tslint:disable-next-line:cyclomatic-complexity
  // eslint-disable-next-line complexity
  setPolarisExpenseRatio(fund: any) {
    let expenseRatioValue = null
    if (
      PerformanceTableComponent.multiAssetTargetDateSet.has(fund.portId) &&
      fund.trustType === COLLECTIVE_INVESTMENT_TRUST
    ) {
      expenseRatioValue = null
    } else if (ClassificationRulesService.isStableFund(fund)) {
      expenseRatioValue = null
    } else if (
      fund.fundFullName &&
      fund.fundFullName.indexOf(TARGET) > NUMBER_MINUS_ONE &&
      fund.fundFullName.indexOf(TRUST) > NUMBER_MINUS_ONE &&
      fund.productTypeLevel2 === COLLECTIVE_INVESTMENT_TRUST
    ) {
      expenseRatioValue = null
    } else if (fund.expenseList !== undefined || fund.feeList !== undefined) {
      const expenseArray = fund.expenseList
      if (expenseRatioValue === null) {
        // eslint-disable-next-line complexity
        expenseArray.forEach((expenseObj: any) => {
          if (
            expenseObj.code !== undefined &&
            expenseObj.code === ADJEXPRTPC &&
            fund.fundIndicator !== FUND_OF_FUND
          ) {
            expenseRatioValue = +expenseObj.percent
          }
          if (
            expenseObj.code !== undefined &&
            expenseObj.code === ADJWTDEXPC &&
            fund.fundIndicator === FUND_OF_FUND
          ) {
            expenseRatioValue = +expenseObj.percent
          }
        })
      }
    }

    return expenseRatioValue
  }

  /**
   *     setPolarisPerformanceReturns
   *     Set performanceReturnValues for each fund for Quarterly and Monthly
   * */
  // tslint:disable-next-line:cyclomatic-complexity
  // eslint-disable-next-line complexity,max-statements
  setPolarisPerformanceReturns(
    fund: any,
    polarisPerformanceReturns: any,
    type: string,
  ) {
    let performanceReturnValues: any
    if (type === MONTH_END_RETURN_NAV || type === MONTH_END_RETURN_MARKET_ETF) {
      this.returnType = MONTH_END_RETURN
      performanceReturnValues = fund.performanceReturnDataMonthly
    }
    if (
      performanceReturnValues !== undefined &&
      performanceReturnValues.returns !== undefined
    ) {
      const tempReturns = performanceReturnValues.returns
      let firstIt = true
      let checkTempclass = RTOT
      for (const it1 of tempReturns) {
        if (firstIt) {
          this.annualAsOfDt = it1.effectiveDate
          firstIt = false
        }
        const tempReturnType = it1.returnType
        const tempReturnPeriod = it1.returnPeriod
        const tempReturnClass = it1.returnClass
        const tempReturnPercent = it1.returnPercent
        if (tempReturnClass === RFEE) {
          checkTempclass = RFEE
        }
        if (
          (this.displayDataType === MARKET && tempReturnClass === RMKT) ||
          (this.displayDataType === 'NAV' && tempReturnClass === checkTempclass)
        ) {
          if (tempReturnPeriod === '1YR' && tempReturnType === CMLT) {
            polarisPerformanceReturns.oneyear = tempReturnPercent
          } else if (tempReturnType === 'ANNL') {
            if (tempReturnPeriod === '3YR') {
              polarisPerformanceReturns.threeyear = tempReturnPercent
            } else if (tempReturnPeriod === '5YR') {
              polarisPerformanceReturns.fiveyear = tempReturnPercent
            } else if (tempReturnPeriod === '10YR') {
              polarisPerformanceReturns.tenyear = tempReturnPercent
            } else if (tempReturnPeriod === 'ITD') {
              polarisPerformanceReturns.sinceInceptionRtn = tempReturnPercent
            }
          }
        }
      }
    }
    return polarisPerformanceReturns
  }

  /**
   *     setPolarisYTDReturnsData
   *     set YTD Returns value and ytdAsOf Date
   * */
  // tslint:disable-next-line:cyclomatic-complexity
  // eslint-disable-next-line complexity
  setPolarisYTDReturnsData(fund: any, ytdReturnsData: any) {
    const ytdReturnValues = fund.performanceReturnDataDaily
    if (
      ytdReturnValues !== undefined &&
      ytdReturnValues.returns !== undefined
    ) {
      for (const it1 of ytdReturnValues.returns) {
        if (it1.returnPeriod === 'CYTD' && it1.returnType === CMLT) {
          if (this.displayDataType === 'NAV') {
            if (it1.returnClass === RTOT && it1.returnPercent !== 0) {
              ytdReturnsData.ytdReturns = it1.returnPercent
              this.ytdAsOf = it1.effectiveDate
            }
          } else if (this.displayDataType === MARKET) {
            if (it1.returnClass === RMKT && it1.returnPercent !== 0) {
              ytdReturnsData.ytdReturns = it1.returnPercent
              this.ytdAsOf = it1.effectiveDate
            }
          }
        }
      }
    }
    return ytdReturnsData
  }

  /**
   *     setPolarisYieldsData
   *     set Yields Data for Funds / Real Estate
   * */
  setPolarisYieldsData(fund: any, yieldsDataReturns: any) {
    const yieldsData = fund.fundYields
    if (yieldsData !== undefined) {
      if (fund.industryCategory === REAL_ESTATE) {
        yieldsDataReturns.secyieldLayers =
          this.secYield.secYieldLayersFund(fund)
        yieldsDataReturns.secYield = this.secYield.setRealEsData(yieldsData)
      } else {
        yieldsDataReturns.secyieldLayers =
          this.secYield.secYieldLayersFund(fund)
        yieldsDataReturns.secYield = this.secYield.setSecYieldDataForFund(
          yieldsData,
          this.performanceCaas,
          this.footNoteCodesMap,
          yieldsDataReturns,
        )
      }
    }
    return yieldsDataReturns
  }

  /**
   *     setPolarisBenchmarkReturns
   *     Set benchmarkReturns for each fund for Quarterly and Monthly
   * */
  // eslint-disable-next-line complexity,max-statements
  setPolarisBenchmarkReturns(
    fund: any,
    polarisPerformanceReturns: any,
    type: string,
  ): any {
    let polarisBenchmarkReturns: {
      benchmarkReturns: any
      benchmarkLongName: any
    }
    if (type === MONTH_END_RETURN_NAV) {
      polarisBenchmarkReturns = fund.benchmark
    }
    if (
      polarisBenchmarkReturns !== undefined &&
      polarisBenchmarkReturns.benchmarkReturns !== undefined &&
      polarisBenchmarkReturns.benchmarkLongName !== undefined
    ) {
      polarisPerformanceReturns.benchmark = {}
      const tempReturns = polarisBenchmarkReturns.benchmarkReturns
      polarisPerformanceReturns.benchmark.benchmarkLongName =
        polarisBenchmarkReturns.benchmarkLongName
      for (const it1 of tempReturns) {
        const tempReturnPeriod = it1.returnPeriod
        const tempReturnType = it1.returnType
        if (tempReturnPeriod === '1YR' && tempReturnType === CMLT) {
          polarisPerformanceReturns.benchmark.oneyear = this.setData(it1)
        } else if (
          tempReturnType === 'ANNL' &&
          tempReturnPeriod !== undefined
        ) {
          if (tempReturnPeriod === '3YR') {
            polarisPerformanceReturns.benchmark.threeyear = this.setData(it1)
          } else if (tempReturnPeriod === '5YR') {
            polarisPerformanceReturns.benchmark.fiveyear = this.setData(it1)
          } else if (tempReturnPeriod === '10YR') {
            polarisPerformanceReturns.benchmark.tenyear = this.setData(it1)
          } else if (tempReturnPeriod === 'ITD') {
            polarisPerformanceReturns.benchmark.sinceInceptionRtn =
              this.setData(it1)
          }
        }
      }
    }
    return polarisPerformanceReturns.benchmark
  }

  /**
   *     setData
   *     set benchmark value
   * */
  setData(returnValue: { returnClass: string; returnPercent: any }) {
    let returnPercent: any
    if (returnValue.returnClass === 'RFEE' || 'RTOT') {
      returnPercent = returnValue.returnPercent
    }
    return returnPercent
  }

  /**
   *     setPopupData
   *     set Pop up Data for Sec Yields Funds
   * */
  setPopupData(performanceViewRow: any) {
    this.secYield.setNotePopupData(
      performanceViewRow,
      JSON.parse(this.aemCassDataFromSession),
      (fundObj: any) => {
        this.fundNameData = fundObj.fundNameData
        this.fundCaasData = fundObj.fundCaasData
          .replaceAll('<br>', '')
          .replaceAll('<p>', '')
          .replaceAll('</p>', '')
        this.fundPolarisData = fundObj.fundPolarisData
        this.notePopupPara = fundObj.notePopupPara
        if (performanceViewRow.secyieldLayers === 'REIT') {
          this.isREIT = true
          this.fundCaasData = fundObj.fundCaasData
        }
      },
    )
  }

  /**
   *     setPopupData
   *     set Empty Pop up Data for Sec Yields Funds
   * */
  setEmptyPopupData() {
    this.fundNameData = ''
    this.fundCaasData = ''
    this.fundPolarisData = ''
    this.notePopupPara = ''
  }

  handleSortEvent({ columnName, isAscending }: TableSortEvent): void {
    // Get the filtered column value
    if (columnName.includes(YTD_RETURN)) {
      columnName = YTD_RETURN_VAL
    }
    columnName = this.filterModel.getSortColumnValue(columnName)
    // If the sorting is not ascending, prepend a minus sign to the column name
    if (!isAscending) {
      columnName = `-${columnName}`
    }
    // Set the sortBy property to the column name
    this.sortBy = columnName
  }

  addTooltip(hover: boolean, index: number) {
    this.hoveredRowIndex = hover ? index : null
  }
  removeTooltip(hover: boolean, index: number) {
    this.hoveredRowIndex = hover ? index : null
  }
  openTooltip(tooltip: TooltipComponent) {
    if (tooltip) {
      tooltip.toggleTooltip()
    }
  }

  private setFactSheetData(fund: any) {
    if (this.factSheetMap.has(fund.portId)) {
      const url = this.factSheetMap.get(fund.portId)
      return url === PERCENT_SIGN ? undefined : url
    }
  }

  downLoadPdf(pdf: string) {
    if (!pdf || pdf.trim() === '' || !pdf.includes('/')) {
      console.error('Invalid PDF URL')
      return
    }
    const fileName = pdf.split('/').pop().split('.')[0].replace(/\+/g, ' ')
    const pdfName = `PDF:${fileName}`
    window.open(pdf, '_blank')
    adobeLaunch.trackContentDownloaded(pdfName, window.location.href)
  }

  trackById(fund: any) {
    return fund.fundID
  }

  updateAnnouncement() {
    this.updateMessage = `Table updated. ${this.performanceView.length} rows now displayed.`
  }
}
